import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import Link from 'next/link';
import React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';

import HeroImageStaff from '@/assets/group-rv-bookings/airstream-tropical-patio.jpg';
import HeroImageEvents from '@/assets/group-rv-bookings/outdoor-festival-aerial.jpg';
import HeroImageFilm from '@/assets/group-rv-bookings/rvs-parked-campground.jpg';
import SoccerImage from '@/assets/group-rv-bookings/stadium-night-lights.jpg';
import HeroImageWeddings from '@/assets/group-rv-bookings/wedding-ceremony-lakeside.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';

const cardMessages = defineMessages({
  weddingsEyebrow: {
    id: 'YkeSQ3',
    defaultMessage: 'Family',
    description: 'Why RVs card eyebrow for weddings',
  },
  weddingsTitle: {
    id: 'Qt+O5c',
    defaultMessage: 'Weddings',
    description: 'Why RVs card title for weddings',
  },
  weddingsDescription: {
    id: 'iBmfMH',
    defaultMessage: 'Comfortable guest stays or private bridal suites right onsite.',
    description: 'Why RVs card description for weddings',
  },
  culturalEventsEyebrow: {
    id: '5+FYQQ',
    defaultMessage: 'Hosting',
    description: 'Why RVs card eyebrow for cultural events',
  },
  culturalEventsTitle: {
    id: 'Fvt9eS',
    defaultMessage: 'Cultural Events',
    description: 'Why RVs card title for cultural events',
  },
  culturalEventsDescription: {
    id: 'C0fkEq',
    defaultMessage: 'Keep vendors, staff, and talent close to the action.',
    description: 'Why RVs card description for cultural events',
  },
  staffHousingEyebrow: {
    id: '/Keanw',
    defaultMessage: 'Short and long-term',
    description: 'Why RVs card eyebrow for staff housing',
  },
  staffHousingTitle: {
    id: 'd77YWY',
    defaultMessage: 'Staff Housing',
    description: 'Why RVs card title for staff housing',
  },
  staffHousingDescription: {
    id: '4Q+REi',
    defaultMessage: 'Simplified on-site housing and logistics for crews.',
    description: 'Why RVs card description for staff housing',
  },
  filmProductionEyebrow: {
    id: 'Bbs5oI',
    defaultMessage: 'On-location',
    description: 'Why RVs card eyebrow for film production',
  },
  filmProductionTitle: {
    id: '677136',
    defaultMessage: 'Film Production',
    description: 'Why RVs card title for film production',
  },
  filmProductionDescription: {
    id: 'JWAtw1',
    defaultMessage: "From talent trailers to mobile offices, our fleet's made for the spotlight.",
    description: 'Why RVs card description for film production',
  },
  soccerEyebrow: {
    id: 'aJhGPb',
    defaultMessage: '2026',
    description: 'Why RVs card eyebrow for World Soccer',
  },
  soccerTitle: {
    id: 'I3M+0L',
    defaultMessage: 'World Soccer',
    description: 'Why RVs card title for World Soccer',
  },
  soccerDescription: {
    id: 'cm++Dv',
    defaultMessage: 'Everything groups of soccer fans traveling to games need to know.',
    description: 'Why RVs card description for World Soccer',
  },
});

export type TWhyRvCard = {
  key: string;
  image: { src: string };
  eyebrow: string | MessageDescriptor;
  title: string | MessageDescriptor;
  description: string | MessageDescriptor;
  path: string;
};

export const DEFAULT_WHY_RV_CARDS: TWhyRvCard[] = [
  {
    key: 'weddings',
    image: HeroImageWeddings,
    eyebrow: cardMessages.weddingsEyebrow,
    title: cardMessages.weddingsTitle,
    description: cardMessages.weddingsDescription,
    path: '/group-rv-bookings/weddings',
  },
  {
    key: 'cultural-events',
    image: HeroImageEvents,
    eyebrow: cardMessages.culturalEventsEyebrow,
    title: cardMessages.culturalEventsTitle,
    description: cardMessages.culturalEventsDescription,
    path: '/group-rv-bookings/cultural-events',
  },
  {
    key: 'staff-housing',
    image: HeroImageStaff,
    eyebrow: cardMessages.staffHousingEyebrow,
    title: cardMessages.staffHousingTitle,
    description: cardMessages.staffHousingDescription,
    path: '/group-rv-bookings/staff-housing',
  },
  {
    key: 'film-production',
    image: HeroImageFilm,
    eyebrow: cardMessages.filmProductionEyebrow,
    title: cardMessages.filmProductionTitle,
    description: cardMessages.filmProductionDescription,
    path: '/group-rv-bookings/film-production',
  },
];

export const SOCCER_CARD: TWhyRvCard = {
  key: 'soccer',
  image: SoccerImage as unknown as { src: string },
  eyebrow: cardMessages.soccerEyebrow,
  title: cardMessages.soccerTitle,
  description: cardMessages.soccerDescription,
  path: '/2026-world-soccer',
};

export function getWhyRvCardByKey(key: string): TWhyRvCard | undefined {
  return DEFAULT_WHY_RV_CARDS.find(card => card.key === key);
}

type TGroupRvWhyRVsProps = {
  heading?: React.ReactNode;
  color?: boolean;
};

export const GroupRvWhyRVs: React.FC<TGroupRvWhyRVsProps & { cards?: TWhyRvCard[] }> = ({
  heading,
  cards,
  color = false,
}) => {
  const intl = useIntl();

  const formatMaybeMessage = (value: string | MessageDescriptor) => {
    if (typeof value === 'string') return value;
    return intl.formatMessage(value);
  };

  const defaultCtaLabel = intl.formatMessage({
    id: '/6Jm5F',
    defaultMessage: 'Submit a group booking enquiry',
    description: 'Group bookings primary CTA',
  });

  const defaultHeading = intl.formatMessage({
    id: 'mOiUF9',
    defaultMessage: 'Why RVs are the smartest way to house your cast, crew, or crowd.',
    description: 'Group bookings Why RVs section heading',
  });

  const defaultBodyLead = intl.formatMessage({
    id: 'zZoIvW',
    defaultMessage:
      'From weddings to film sets, cultural festivals to construction sites—group RV rentals give you comfort,',
    description: 'Group bookings Why RVs section body copy (lead clause)',
  });

  const defaultBodyTail = intl.formatMessage({
    id: 'O5BLQ2',
    defaultMessage:
      'flexibility, and control. Forget hotel headaches and long drives between locations. We bring the rooms to you.',
    description: 'Group bookings Why RVs section body copy (tail clause)',
  });

  const learnMoreLabel = intl.formatMessage({
    id: 'rs0u7D',
    defaultMessage: 'Learn more',
    description: 'Label for Why RVs card link',
  });

  return (
    <section className={`${color ? 'bg-canvas-100' : 'bg-white'} py-12 lg:py-16`}>
      <div className="flex justify-center lg:px-12">
        <MaxWidth className="gap-10">
          {/* Heading + body copy */}
          <div className="max-w-3xl mb-3">
            <Heading
              variant={EHeadingStyleVariant.H3}
              className="leading-tight text-gray-900  mb-7 font-[abril-titling]">
              {heading ?? defaultHeading}
            </Heading>
            <Text variant={ETextStyleVariant.MediumRegular} className="text-gray-70">
              {defaultBodyLead}
              <span className="hidden md:inline">
                <br />
              </span>
              {defaultBodyTail}
            </Text>
          </div>

          {/* Cards */}
          {/* On mobile: horizontal scrollable row. On md+: responsive grid. */}
          <div className="flex pb-2 overflow-x-auto gap-4 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible lg:grid-cols-4">
            {(cards ?? DEFAULT_WHY_RV_CARDS).map(card => (
              <Link key={card.key} href={card.path} rel="noopener noreferrer" className="block">
                <div className="flex flex-col flex-shrink-0 h-full text-white cursor-pointer rounded-2xl  md:w-full w-[75vw] sm:w-[70vw] md:flex-shrink transition-transform">
                  <div className="relative overflow-hidden md:w-full w-[75vw] sm:w-[70vw] rounded-2xl aspect-[3/4]">
                    <ResponsiveImage
                      src={card.image.src}
                      alt={formatMaybeMessage(card.title)}
                      className="object-cover w-full h-full rounded-2xl transition duration-300 ease-in-out hover:scale-105 hover:brightness-110"
                    />

                    <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/70 to-black/10 rounded-2xl">
                      <Text className="mb-1 uppercase tracking-[0.16em] text-gray-10 !text-[11px]">
                        {formatMaybeMessage(card.eyebrow)}
                      </Text>
                      <Text className="italic font-[abril-titling] !text-[32px]">
                        {formatMaybeMessage(card.title)}
                      </Text>
                    </div>
                  </div>
                  <div
                    className={`flex flex-col justify-between flex-1 p-3 text-gray-900 ${
                      color ? 'bg-canvas-100' : 'bg-white'
                    }`}>
                    <Text
                      variant={ETextStyleVariant.MediumRegular}
                      className="mb-2 text-[#1D1F20] text-[14px] sm:text-[15px] md:text-[15px]">
                      {formatMaybeMessage(card.description)}
                    </Text>
                    <span className="relative text-sm   font-medium text-left text-green-500 underline hover:underline -top-6">
                      {learnMoreLabel}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <Button
            variant={EButtonColorVariant.Primary}
            className="relative w-auto md:w-[300px] -top-8"
            label={defaultCtaLabel}
            // TODO: Wire up to actual enquiry flow / form when available
            onClick={() => {
              if (typeof window === 'undefined') return;
              const target = document.getElementById('group-bookings-form');
              target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
        </MaxWidth>
      </div>
    </section>
  );
};
